import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { InboxIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";

import { ErrorFallback, Head } from "../../../../components/core";
import { Button } from "../../../../components/form";
import { LimitBy, TableCursor } from "../../../../components/table";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import { GET_CUSTOMERGROUPS } from "../../../../graphql/sales/customer-groups";
import { AlertModal, AlertType, useAlert } from "../../../../hooks/useAlert";
import { formatDate } from "../../../../utils";
import { AuthShield } from "../../../auth/core";

type Customer = {
  id: string;
  customerName: string;
};
type CustomerGroup = {
  id: string;
  name: string;
  description: string;
  customers: Customer[];
  createdAt: string;
  status: boolean;
};

const CustomerGroupList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);
  const { addNotify } = useNotifyContext();

  const { data, loading, error, refetch } = useQuery<{
    fetchCustomerGroups: CustomerGroup[];
  }>(GET_CUSTOMERGROUPS);
  const customerGroups = useMemo<CustomerGroup[]>(
    () =>
      data?.fetchCustomerGroups.flatMap((item) => ({
        ...item,
        createdAt: formatDate(item.createdAt),
      })) || [],
    [data?.fetchCustomerGroups]
  );

  const DELETE_CUSTOMERGROUP = gql`
    mutation CustomerGroupDelete($id: ID!) {
      customerGroupDelete(input: { id: $id }) {
        customerGroup {
          id
          name
        }
      }
    }
  `;
  const [deleteCustomerGroup] = useMutation(DELETE_CUSTOMERGROUP, {
    refetchQueries: [{ query: GET_CUSTOMERGROUPS }],
  });
  const handleDelete = async (id: string) => {
    return await deleteCustomerGroup({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.customerGroupDelete) {
          const deletedCustomerGroup = data?.customerGroupDelete?.customerGroup;
          refetch();
          addNotify({
            type: NotifyType.SUCCESS,
            title: `Customer Group ${deletedCustomerGroup.name} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Customer Group delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "Customer Group delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<CustomerGroup, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 300,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.name,
      },
      {
        accessorKey: "customers",
        header: "Customers",
        cell: ({ row }) => {
          return row.original.customers
            .map((customer) => customer.customerName)
            .join(", ");
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <div className="flex space-x-2 md:space-x-4">
            <AuthShield access={["update-customergroups"]}>
              <Button
                type="link"
                href={`/sales/settings/customer-groups/${props.row.original.id}`}
                variant="icon"
                className="text-blue-500 hover:text-blue-600"
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">Edit, {props.row.original.name}</span>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-customergroups"]}>
              <Button
                variant="icon"
                className="text-red-500 hover:text-red-600"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id,
                    name: props.row.original.name,
                  });
                }}
              >
                <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Delete, {props.row.original.name}
                </span>
              </Button>
            </AuthShield>
          </div>
        ),
      },
    ],
    []
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title="Customer Groups"
        heading="Customer Groups"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Customer Groups",
            href: "/sales/settings/customer-groups",
          },
          {
            name: CustomerGroupListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-5 sm:flex sm:items-end">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {CustomerGroupListResource.name}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {CustomerGroupListResource.description}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-customergroups"]}>
              <Button type="link" href="/sales/settings/customer-groups/add">
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add customer group
              </Button>
            </AuthShield>
          </div>
        </div>

        <TableCursor
          data={customerGroups}
          columns={columns}
          loading={loading}
          totalRows={customerGroups.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No customer groups found"
          fallbackText="You can add a new customer group by clicking on add customer group button."
        />
      </div>
    </>
  );
};

export default CustomerGroupList;
export const CustomerGroupListResource: ResourceProps = {
  name: "Customer Groups",
  description: "A list of customer groups",
  access: ["read-customergroups"],
  path: "customer-groups",
};
